import { useCallback, useMemo, useState } from "react";
import FeatureKeys from "global/constants/FeatureKeys";
import strings from "global/constants/StringConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { useFeatureState } from "hooks/use-feature";
import { selectId } from "redux/authSlice";
import { useAppSelector } from "redux/hooks";
import {
  CompareRouterState,
  compareUserKey,
} from "screens/ContractCompare/ContractCompareTypesAndValidation";
import history from "utils/history";
import { CompareLock, MatchedContract } from "./ContractSearchTypes";

/**
 * The entry points from Contract Search into Compare contract versions (the control
 * beside each matched PDF / Word contract): whether they are locked, where they lead, and the upgrade modal's
 * open state.
 *
 * Contract Search itself stays open to every plan; only these entries look locked when the plan has no AI
 * Assistant -- the same feature the compare call is gated on. A locked click opens the upgrade modal instead of
 * navigating. An administrator's switch-off looks locked too (as in the side nav) but still opens the page,
 * which shows the administrator's own notice.
 */
export const useCompareEntry = () => {
  const aiState = useFeatureState(FeatureKeys.AI_ASSISTANT);
  // The pick travels in router state tied to this user (D58 c).
  const userKey = compareUserKey(useAppSelector(selectId));
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const lock = useMemo<CompareLock | null>(() => {
    if (aiState.status === strings.FEATURE_STATUS_AVAILABLE) return null;
    if (aiState.status === strings.FEATURE_STATUS_ADMIN_DISABLED) {
      const message =
        "disabledMessage" in aiState ? aiState.disabledMessage : undefined;
      return {
        kind: "admin",
        message:
          message || strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP,
      };
    }
    return { kind: "plan" };
  }, [aiState]);

  /** Opens the compare page with the contract's file pre-picked as the old version, through router state (path and query stay clean). */
  const openCompare = useCallback(
    (contract: MatchedContract) => {
      if (lock?.kind === "plan") {
        setUpgradeOpen(true);
        return;
      }
      const state: CompareRouterState = {
        documentId: contract.documentId,
        fileName: contract.fileName,
        userKey,
      };
      history.push(viewpaths.contractCompareViewPath, state);
    },
    [lock, userKey],
  );

  const closeUpgrade = useCallback(() => setUpgradeOpen(false), []);

  return { lock, upgradeOpen, closeUpgrade, openCompare };
};
